import type { PlayerProfile } from "@wr/contracts";
import type { SessionUser } from "../lib/auth";

/**
 * The splash's routing matrix — **LOCKED** (design spec §5).
 *
 * | Session                                    | Destination                |
 * |--------------------------------------------|----------------------------|
 * | none                                       | `/login` (keeping `?next=`)|
 * | verified: no, guest: no                    | `/verify`                  |
 * | nickname not confirmed                     | `/welcome`                 |
 * | guest, nickname not confirmed              | `/welcome` (guest variant) |
 * | complete                                   | `?next=` or `/lobby`       |
 *
 * It lives here, alone and pure, because it is asked the same question from
 * three places — the splash, every route guard, and the post-login redirect —
 * and three copies of a five-way branch is three chances to disagree about
 * whether a guest needs to verify an email they do not have.
 */

export type Stage = "anonymous" | "unverified" | "needs-identity" | "ready";

export const STAGE_ROUTE: Record<Exclude<Stage, "ready">, string> = {
  anonymous: "/login",
  unverified: "/verify",
  "needs-identity": "/welcome",
};

/** Where a completed session lands when no `?next=` says otherwise. */
export const HOME_ROUTE = "/lobby";

export function stageFor(session: SessionUser | null, profile: PlayerProfile | null): Stage {
  if (!session || !profile) return "anonymous";

  /**
   * A guest has no email to verify — the anonymous plugin gives them a
   * synthetic `@guest.wordyroyale.com` address and `emailVerified: false`.
   * Sending them to `/verify` would be an infinite loop through a screen whose
   * only action is "resend a mail to an address that does not exist".
   */
  if (!session.isAnonymous && !session.emailVerified) return "unverified";

  if (!profile.identityConfirmed) return "needs-identity";
  return "ready";
}

/**
 * `?next=` survives the whole chain (architecture §7) — but only if it points
 * at us.
 *
 * An off-origin `next` is dropped **silently**: a redirect parameter is an open
 * redirect waiting to happen, and there is nothing useful to tell a player
 * about a link somebody else crafted for them. Protocol-relative (`//evil`) is
 * the case a naive `startsWith("/")` misses.
 *
 * ── Full parity with the server (S3-6 item 4, T-190 rider 1, QA T-175 N3) ───
 *
 * This is now the API's `safeReturnPath` (`apps/api/src/auth/verify-link.ts`)
 * clause for clause: trim, non-empty, at most 512 characters, no backslashes,
 * no control characters, exactly one leading slash.
 *
 * None of the closed holes was a defect on its own — `/\evil.example.com`, a
 * 3000-character path and `" /lobby"` all ended at a harmless same-origin 404,
 * so the two guards disagreed only in the safe direction. They are closed
 * because **an allow-list with exceptions is not an allow-list**, and because
 * these two functions now answer the same question in two tiers: this one picks
 * the screen the browser routes to, `safeReturnPath` picks the path that goes
 * into an email (T-190 threads `next` into every verification mail). While they
 * agree, "is this `next` acceptable?" has one answer and an audit reads one
 * rule; where they drift is where the next report will point.
 *
 * What each clause is for:
 *
 *  - **trim** — leading whitespace is the difference between `" /lobby"` being
 *    refused here and accepted by the server: exactly the sort of disagreement
 *    this exists to remove;
 *  - **`\`** — WHATWG URL parsing folds it into `/`, so `/\evil.example.com` is
 *    a protocol-relative URL wearing a disguise. Rejected, never translated —
 *    translating would silently change the meaning of an honest path;
 *  - **control characters** — nothing that could be smuggled into a header, a
 *    log line or a mail body downstream;
 *  - **512** — the server's bound, because this value can reach an inbox.
 *
 * The server stays authoritative regardless: this copy exists to stop the
 * browser routing somewhere silly, not to protect the mail.
 */
// eslint-disable-next-line no-control-regex -- rejecting them is the point
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;
const MAX_NEXT_LENGTH = 512;

export function sanitiseNext(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  if (value.length === 0 || value.length > MAX_NEXT_LENGTH) return null;
  if (value.includes("\\")) return null;
  if (CONTROL_CHARACTERS.test(value)) return null;
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  return value;
}

/** Appends `?next=` to a destination, preserving it through the chain. */
export function withNext(path: string, next: string | null): string {
  if (!next) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}next=${encodeURIComponent(next)}`;
}

/**
 * The `?next=` a redirect must carry **forward**, read off the URL it is
 * leaving.
 *
 * ── Why this exists (QA T-172 finding B1) ───────────────────────────────────
 *
 * Every guard and every post-auth screen used to hand `destinationFor` a
 * literal `null` here, which is a quiet way of saying "there is no deep link"
 * on a URL that is holding one. It cost the whole contract: logging in from
 * `/login?next=%2Fsettings` landed on `/lobby`, and continuing as a guest from
 * the same URL reached a bare `/welcome`.
 *
 * `null` is now something a call site has to type on purpose. Anything that
 * redirects mid-chain calls this instead, so "`?next=` survives the whole
 * chain" (design spec §5, LOCKED) is one function rather than six remembered
 * decisions.
 */
export function carriedNext(search: string): string | null {
  return sanitiseNext(new URLSearchParams(search).get("next"));
}

export function destinationFor(stage: Stage, next: string | null): string {
  if (stage === "ready") return next ?? HOME_ROUTE;
  return withNext(STAGE_ROUTE[stage], next);
}
